import { createSlice } from '@reduxjs/toolkit'

import { actions as actionActionsF } from './actions'
import { actions as localActionsF } from './local'
import { actions as surveyActionsF } from './surveys'
import orderActions from 'FlowTaker/lib/orderActions'
import { toast } from 'react-toastify'
import { restClientRaw } from 'Shared/hooks/restClient'

import { deepSnakeCase } from 'Shared/functions/util'

const reducer = createSlice({
  name: 'builder',
  initialState: {
    templateFields: {},
    validationErrors: []
  },
  reducers: {
    set: (s, a) => a.payload,
    upsert: (s, a) => ({ ...s, ...a.payload }),
    setTemplateField: (s, a) => {
      s.flow.templateFields[a.payload.slug] = a.payload.value
    },
    updateFlowSetting: (s, a) => {
      s.flow.settings[a.payload.key] = a.payload.value
    },
    upsertFlowTemplate: (s, a) => {
      s.flowTemplate = { ...s.flowTemplate, ...a.payload }
    },
    updateFlow: (s, a) => {
      s.flow[a.payload.key] = a.payload.value
    },
    setValidationErrors: (s, a) => {
      s.validationErrors = a.payload
    },
  },
}).reducer

const actions = (dis, store, restClient) => {
  const actionActions = actionActionsF(dis, store, restClient)
  const localActions = localActionsF(dis, store, restClient)
  const surveyActions = surveyActionsF(dis, store, restClient)

  function load({ flowId, flowTemplateId, isEditingTemplate = false, nav }) {
    localActions.upsert({ flowId, flowTemplateId, isEditingTemplate })

    if (nav) localActions.setNav(nav)
    if (isEditingTemplate) {
      localActions.setNav('template')
    }

    return loadData()
  }

  function loadData() {
    localActions.upsert({ loaded: false })
    const state = store.getState()
    const { flowId, flowTemplateId } = state.local

    const promises = []
    promises.push(loadFlow(flowId))
    promises.push(loadFlowTemplate(flowTemplateId))
    promises.push(loadMergeTags())
    return Promise.all(promises).then(() => localActions.upsert({ loaded: true }))
  }

  function loadFlow(flowId) {
    return restClient.get(`/api/flows/${flowId}`)
      .then(({ data: flow }) => {
        handleFlowData(flow)
        selectAction(flow.actions.find(a => a.isStart)?.id)
      })
  }

  function loadFlowTemplate(flowTemplateId) {
    return restClient.get(`/api/flow_templates/${flowTemplateId}`)
      .then(({ data: flowTemplate }) => {
        handleFlowTemplateData(flowTemplate)
      })
  }

  function handleFlowData(flow) {
    // Fields which are merged in Liquid templates need to be snake cased
    const templateFields = deepSnakeCase(flow.templateFields)
    const flowFields = deepSnakeCase(flow.flowFields)

    const snakeFlow = { ...flow, templateFields, flowFields }

    dis({ type: 'builder/upsert', payload: { flow: snakeFlow } })
    flow.actions.forEach(a => {
      if (a.kind === 'survey') {
        surveyActions.load(a.surveyId)
      }
    })

    dis({ type: 'actions/setAll', payload: flow.actions })
    dis({ type: 'actionTemplates/setAll', payload: _.flatMap(flow.actions, action => action.actionTemplates || []) })

    localActions.setDirty(false)
  }

  function handleFlowTemplateData(flowTemplate) {
    dis({ type: 'builder/upsert', payload: { flowTemplate } })
  }

  function loadMergeTags() {
    return restClientRaw.get(`/api/render/available_merge_tags`)
      .then(({ data: mergeTags }) => {
        dis({ type: 'builder/upsert', payload: { mergeTags } })
      })
  }

  function selectAction(actionId, { pageNumber } = {}) {
    const state = store.getState()
    if (state.local.navItem === 'setup') {
      actionId = null
    }
    if (typeof(pageNumber) === 'undefined') {
      if (state.local.selectedActionId === actionId) {
        var selectedPageNumber = state.local.selectedPageNumber
      } else {
        var selectedPageNumber = 1
      }
    } else {
      var selectedPageNumber = pageNumber
    }

    dis({ type: 'local/upsert', payload: { selectedActionId: actionId, selectedPageNumber } })
  }

  function selectQuestion(questionId) {
    dis({ type: 'local/upsert', payload: { selectedQuestionId: questionId } })
  }

  function setTemplateField(payload) {
    localActions.setDirty(true)
    dis({ type: 'builder/setTemplateField', payload })
  }

  function updateFlowSetting(key, value) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlowSetting', payload: { key, value } })
  }

  function updateFlow(key, value) {
    dis({ type: 'builder/updateFlow', payload: { key, value } })
  }

  function upsertFlowTemplate(payload) {
    dis({ type: 'builder/upsertFlowTemplate', payload })
  }

  function persistAll() {
    const state = store.getState()
    const entities = state.actions.entities
    const orderedActions = orderActions(_.values(entities))
    const currentActionId = state.local.selectedActionId
    const isEditingTemplate = state.local.isEditingTemplate
    const currentActionsIndex = orderedActions.findIndex(a => a.id === currentActionId)
    const actions = _.values(state.actions.entities)

    const promises = []
    actions.forEach(action => {
      if (action.kind === 'survey' && action.surveyId) {
        promises.push(surveyActions.persistQuestions(action.surveyId))
      }
    })
    promises.push(persistFlow())
    if (isEditingTemplate) promises.push(persistFlowTemplate())

    return Promise.all(promises)
      .then(() => localActions.setDirty(false))
      .then(() => {
        const currentState = store.getState()
        const entities = currentState.actions.entities
        const orderedActions = orderActions(_.values(entities))
        const currentActionId = currentState.local.selectedActionId

        if (!entities[currentActionId]) {
          const lastActionId = orderedActions[currentActionsIndex]?.id
          return selectAction(lastActionId)
        }
        return selectAction(currentActionId)
      })
  }

  const persistFlowFields = ['name', 'slug', 'settings', 'templateFields', 'additionalHead', 'additionalCss']

  function persistFlow() {
    const state = store.getState()
    const flow = _.pick(state.builder.flow, persistFlowFields)
    const actions = _.values(state.actions.entities).map(action => {
      const newAction = { ...action }
      if (newAction.kind === 'email_to_target') {
          newAction.actionTemplates = _.values(state.actionTemplates.entities).filter(at => at.actionId === action.id)
      }

      return newAction
    })
    const flowId = state.builder.flow.id

    return restClient.put(`/api/flows/${flowId}`, { flow, actions })
      .then((response) => {
        dis({ type: 'local/clearErrors', payload: 'flow' })
        handleFlowData(response.data)
      }).catch((error) => {
        console.error('Error saving flow:', error)
        toast.error('Failed to save. Please check the details you entered')
        dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
      })
  }

  function persistFlowTemplate() {
    const state = store.getState()
    const flowTemplate = state.builder.flowTemplate
    const flowTemplateId = flowTemplate.id

    const flowTemplateParams = _.pick(flowTemplate, ['name', 'description', 'enabledForNewPages', 'bodyHtml', 'headHtml', 'stylesCss', 'fields'])

    return restClient.put(`/api/flow_templates/${flowTemplateId}`, { flowTemplate: flowTemplateParams })
      .then((response) => {
        dis({ type: 'local/clearErrors', payload: 'flowTemplate' })
        handleFlowTemplateData(response.data)
      }).catch((error) => {
        toast.error('Failed to save. Please check the details you entered')
        dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
      })
  }

  function setValidationErrors(errors) {
    dis({ type: 'builder/setValidationErrors', payload: errors })
  }

  function cloneFlowTemplate(name) {
    const state = store.getState()
    const flowTemplateId = state.builder.flowTemplate.id

    return restClient.post(`/api/flow_templates/${flowTemplateId}/clone`, {
      flow_template: {name}
    })
    .then(({ data: flowTemplate }) => {
      window.location.href = `/admin/settings/flow_templates/${flowTemplate.id}/edit`
    })
    .catch((error) => {
      toast.error('Failed to clone template. Please try again.')
      dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
    })
  }


  return {
    load,
    loadData,
    selectAction,
    selectQuestion,
    setTemplateField,
    updateFlowSetting,
    updateFlow,
    upsertFlowTemplate,
    persistAll,
    setValidationErrors,
    cloneFlowTemplate,
  }
}

export { reducer, actions }
export default { reducer, actions }
